import { getBuyPathFromUrl } from './utils';
import {
  CUSTOM_PARAMS_STORAGE_KEY,
  MKTTRACK_COOKIE_NAME,
  MKTSESSION_COOKIE_NAME,
} from '../../resources/constants';
import { setParams } from './index';
import { gtagWrapper, getGa4GroupId } from '../../google-analytics/utils';
import { createConfigParametersRepository } from '../../../configParametersRepository';

interface SearchParams {
  buyPath: string;
  [key: string]: string;
}

/**
 * Retrieves the buy path parameter from the URL, session storage, or returns an empty string if not found.
 *
 * @returns {string} The buy path parameter value.
 */
export const getBuyPathParam = (): string => {
  return getBuyPathFromUrl() || getBuyPathFromSessionStorage() || '';
};

const getBuyPathFromSessionStorage = (): string | undefined => {
  const { getTrackingStorageClient } = createConfigParametersRepository();
  const storageClient = getTrackingStorageClient();
  const searchParams: SearchParams = storageClient?.getFromStorage(
    'sessionStorage',
    'searchParameters',
    undefined
  );
  return searchParams?.buyPath;
};

/**
 * This function retrieves the 'buyPath' parameter from the URL hash or from the session storage.
 * If the 'buyPath' parameter is found in the URL hash, it is returned.
 * If the 'buyPath' parameter is not found in the URL hash, the function checks the session storage.
 * If the 'buyPath' parameter is found in the session storage, it is returned.
 * If the 'buyPath' parameter is not found in either the URL hash or the session storage, an empty string is returned.
 *
 * */
export const setBuyPathParam = () => {
  const buyPath = getBuyPathParam();
  setParams({ customDimension: '73', value: buyPath });
};

/**
 * Updates the 'mkttrack' and 'mktsession_id' field in the session storage with the values of the 'mktTrack' and 'mktSession' cookies.
 * If they are not found, the fields are not updated.
 *
 * @function setMktTrackParam
 */
export const setMktTrackParam = () => {
  const { getTrackingStorageClient } = createConfigParametersRepository();
  const storageClient = getTrackingStorageClient();
  const mktTrackCookie = storageClient?.getCookie(MKTTRACK_COOKIE_NAME);
  const mktSessionCookie = storageClient?.getCookie(MKTSESSION_COOKIE_NAME);

  if (mktTrackCookie || mktSessionCookie) {
    storageClient?.update(CUSTOM_PARAMS_STORAGE_KEY, (existingData = {}) => ({
      ...existingData,
      ...(mktTrackCookie && { mkttrack: mktTrackCookie }),
      ...(mktSessionCookie && { mktsession_id: mktSessionCookie }),
    }));
  }
};

/**
 * This function sets the 'client_id' custom parameter in the custom dimensions repository.
 * It uses the Google Analytics wrapper to get the 'clientId' from the current tracker.
 * The 'client_id' custom parameter is then set with the value of the 'clientId'.
 *
 * @function setClientIdParam
 */
export const setClientIdParam = () => {
  const ga4GroupId = getGa4GroupId();
  gtagWrapper('get', ga4GroupId, 'client_id', (clientId: string) => {
    setParams({ customParam: 'client_id', value: clientId.toString() });
  });
};
